import {
  FDContentContainer, FDStack, FDImage, FDBox, FDBodyText, FDPaper, FDPrimaryActionButton,
} from '@facet-wealth/design';
import React from 'react';
import fr from 'facet-rest';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import Footer from 'Components/ClientApp/Desktop/Footer';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { dataAttrs } from 'Helpers/Testing';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useCopyToClipboard } from 'Helpers/Utils';
import StepNavigationFooter from 'Packages/FacetDesign/Components/Experimental/StepNavigationFooter';
import throwInDevOutsideReact from 'Helpers/throwInDevOutsideReact';

const useStyles = buildUseStyles('ReferralPage', () => ({
  footer: {
    position: 'sticky',
    transform: 'initial',
    bottom: 'unset',
  },
  wrapText: {
    wordWrap: 'break-word',
  },
}));

export const Layout = () => {
  const { resource } = useMetaLayoutContext();
  const isMobile = useMobileLayout();
  const classes = useStyles();
  const { copyToClipboard } = useCopyToClipboard();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { displayErrorMessage } = useSnackbarContext();

  useBoneBackgroundColor();

  const onCopy = async (relationship) => {
    logStandardizedEvent(categories.referralPage, eventTypes.clickButton, `Click copy ${relationship.meta('type') || 'link'}`);
    try {
      await relationship.link('related').post(); // Record that the user clicked share
    } catch (err) {
      displayErrorMessage(err, err.message);
    }
  };

  const handleClick = async (rel) => {
    // We must copy to the clipboard before we attempt to do any other async operations because
    // copying to the clipboard must happen as the direct result of user input and cannot happen
    // as the result of an async call. See: https://webkit.org/blog/10855/async-clipboard-api/
    await copyToClipboard(rel.meta('clipboardText'));
    await onCopy(rel);
  };

  const handleOpenNativeShare = async (rel) => {
    if (navigator?.share) {
      try {
        logStandardizedEvent(categories.referralPage, eventTypes.clickButton, `Click copy ${rel.meta('type') || 'link'}`, { eventProperties: { type: 'nativeShare' } });
        await navigator.share({
          title: resource.attr('messageTitle'),
          text: rel.meta('clipboardText'),
          url: resource.attr('referralLink'),
        });
      } catch (err) {
        // when the user closes the share sheet without sharing, an AbortError is thrown.
        // These are expected, so we don't sent them to Sentry.
        if (err.name !== 'AbortError') {
          // all other error types are sent to sentry and the error message will appear in the snackbar
          throwInDevOutsideReact(err);
          displayErrorMessage('An unexpected error occurred. Please try again later.');
        }
      }
    }

    if (!navigator.share) {
      await handleClick(rel);
    }
  };

  const handleClickShareFacetCTA = async (rel) => {
    await resource.withRelationship(fr.relationships.showNativeShareSheet.name, () => {
      return handleOpenNativeShare(rel);
    }, () => {
      return handleClick(rel);
    });
  };

  const imageRelName = isMobile ? fr.relationships.imageMobile.name : fr.relationships.imageDesktop.name;

  return (
    <>
      <FDContentContainer maxWidth={'md'}>
        <FDStack direction={'column'} spacing={4} paddingY={isMobile ? 4 : 8} alignItems={'center'}>
          <FDStack direction={'column'} spacing={isMobile ? 2 : 3} flex={1} width={'100%'}>

            <FDStack direction={'column'} spacing={5}>
              <MetaControlField attr={'title'} {...dataAttrs('referral page title')} />
              {resource.withRelationship(imageRelName,
                (rel) => (
                  <FDStack flex={1}>
                    <FDImage
                      src={rel.link('related').url()}
                      alt={rel.meta('altText') || ''}
                      width={'100%'}
                      objectFit={'cover'}
                      objectPosition={'center'}
                    />
                  </FDStack>
                ),
                () => null)}
              <MetaControlField attr={'body'} {...dataAttrs('referral page body')} />
            </FDStack>

            {resource.withRelationship(fr.relationships.referralLink.name, (rel) => (
              <FDStack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={'center'} justifyContent={'center'} paddingY={2}>

                <FDPaper width={isMobile ? '100%' : '50%'} padding={2}>
                  <FDBodyText maxLines={1} value={resource.attr('referralLink')} {...dataAttrs('referral link')} />
                </FDPaper>

                <FDPrimaryActionButton
                  label={rel.meta('prompt')}
                  isFullWidth={isMobile}
                  onClick={() => handleClick(rel)}
                  {...dataAttrs('copy referral link btn')}
                />

              </FDStack>
            ), () => null)}
            <FDStack direction={'column'}>
              <MetaControlField attr={'attestation'} {...dataAttrs('referral page attestation')} />
              <MetaControlField attr={'disclosure'} {...dataAttrs('referral page disclosure')} />
              <MetaControlField attr={'termsAndConditions'} {...dataAttrs('referral page terms and conditions')} />
            </FDStack>

          </FDStack>

        </FDStack>
        <MetaControlField attr={'howToInfo'} />
        <MetaControlField attr={'details'} />
        <MetaControlField attr={'termsAndConditions'} {...dataAttrs('referral page terms and conditions 2')} />
        <FDBox height={'16px'} />
      </FDContentContainer>
      {isMobile && resource.withRelationship(fr.relationships.referralMessage.name, (rel) => (
        <StepNavigationFooter
          onNext={() => handleClickShareFacetCTA(rel)}
          label={rel.meta('prompt')}
          isMobileNavShown
        />
      ), () => null)}
      {!isMobile && <Footer className={classes.footer} />}
    </>
  );
};
